import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  XMarkIcon,
  UserGroupIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function DonnerPouvoirModal({ isOpen, onClose, reunionId, onSuccess }) {
  const signatureRef = useRef(null);
  const [search, setSearch] = useState('');
  const [candidats, setCandidats] = useState([]);
  const [loadingCandidats, setLoadingCandidats] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setSelected(null);
    setSignature('');
    setError('');
    signatureRef.current?.clear();
    fetchCandidats('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reunionId]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => fetchCandidats(search), 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isOpen, reunionId]);

  const fetchCandidats = async (q) => {
    try {
      setLoadingCandidats(true);
      const token = localStorage.getItem('token');
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/mine/${reunionId}/mandataires-candidats${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCandidats(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des adhérents');
    } finally {
      setLoadingCandidats(false);
    }
  };

  const handleSignature = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setSignature('');
      return;
    }
    setSignature(signatureRef.current.toDataURL());
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    setSignature('');
  };

  const handleSubmit = async () => {
    if (!selected) {
      alert('Veuillez sélectionner un mandataire');
      return;
    }
    const signatureData = signatureRef.current?.isEmpty()
      ? ''
      : signatureRef.current.toDataURL();
    if (!signatureData) {
      alert('Veuillez signer le document');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/mine/${reunionId}/pouvoirs`,
        {
          mandataireUserId: selected.userId,
          signature: signatureData,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement du pouvoir');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Donner pouvoir</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Choisir le mandataire *
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Adhérent actif {new Date().getFullYear()}, convoqué à cette réunion.
              </p>
              <div className="relative">
                <input
                  type="text"
                  value={selected ? `${selected.prenom} ${selected.nom}` : search}
                  onChange={(e) => {
                    setSelected(null);
                    setSearch(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Rechercher par nom ou prénom..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-honey-500 focus:border-honey-500"
                />
                {showDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
                    <div className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-52 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto">
                      {loadingCandidats ? (
                        <div className="px-4 py-3 text-sm text-gray-500">Chargement...</div>
                      ) : candidats.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">Aucun adhérent trouvé</div>
                      ) : (
                        candidats.map((c) => (
                          <button
                            key={c.userId}
                            type="button"
                            disabled={!c.selectable}
                            onClick={() => {
                              if (!c.selectable) return;
                              setSelected(c);
                              setSearch('');
                              setShowDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm ${
                              c.selectable
                                ? 'hover:bg-honey-50 text-gray-900 cursor-pointer'
                                : 'text-gray-400 cursor-not-allowed bg-gray-50'
                            }`}
                          >
                            <div className="font-medium">{c.prenom} {c.nom}</div>
                            {!c.selectable && c.reason && (
                              <div className="text-xs text-red-500 mt-0.5">{c.reason}</div>
                            )}
                            {c.selectable && c.pouvoirsCount > 0 && (
                              <div className="text-xs text-honey-600 mt-0.5">
                                {c.pouvoirsCount} pouvoir{c.pouvoirsCount > 1 ? 's' : ''} déjà reçu{c.pouvoirsCount > 1 ? 's' : ''}
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-900">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-red-600" />
              <p>
                Assurez-vous auprès de cette personne qu&apos;elle prévoit d&apos;être présente à
                cette réunion. Si elle est absente, vous ne serez pas représenté à cette réunion.
              </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <EnvelopeIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                La personne choisie sera notifiée par e-mail. Cette action est définitive
                (pas de révocation possible).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature *
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-32 bg-white',
                  }}
                  onEnd={handleSignature}
                />
              </div>
              <button
                type="button"
                onClick={clearSignature}
                className="mt-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Effacer la signature
              </button>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !selected || !signature}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:opacity-50"
            >
              <UserGroupIcon className="h-4 w-4" />
              {submitting ? 'Enregistrement…' : 'Confirmer le pouvoir'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
